import React, { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { Feed } from './Feed'
import { PostCreateButton } from '../../components/feed/PostCreateButton'
import { ScheduledPostsBanner } from '../../components/feed/ScheduledPostsBanner'
import { ErrorView } from '../error/ErrorView'
import { ButtonVariant } from '../../components/common/Button'
import { reverseUrl } from '../../navigation/reverseUrl'
import { IllustrationChoices } from '../../components/common/Illustration'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { CreateEditPostModal } from '../../components/feed/CreateEditPostModal'
import { useModal } from '../../hooks/useModal'
import { ISocietiesModel } from '../../interfaces/models/societies.interfaces'
import { PostRow } from '../../generated/api/post-row'
import { SpinnerWrapper } from '../../components/common/SpinnerWrapper'
import { i18nSocietyType } from '../../i18n'

const FeedContainer = ({
  userId,
  isAllSocietiesLockedForUserErrorView,
  societiesWhereUserCanPost,
  isPendingRequestView,
  noSocieties,
  posts,
  fetchNextPage,
  isFilterActive,
  isLoading,
  isFetching,
  hasFeedError,
  retryFeed,
}: {
  userId: string
  isPendingRequestView: boolean
  isAllSocietiesLockedForUserErrorView: boolean
  noSocieties: boolean
  societiesWhereUserCanPost: ISocietiesModel[]
  posts: PostRow[]
  fetchNextPage: () => void
  isFilterActive: boolean
  isLoading: boolean
  isFetching: boolean
  hasFeedError: boolean
  retryFeed: () => void
}): ReactNode => {
  const navigate = useNavigate()
  const { translate } = useAppTranslation()
  const {
    show: showCreatePostModal,
    open: openCreatePostModal,
    close: closeCreatePostModal,
  } = useModal({ preventClose: true })

  if (isLoading) {
    return <SpinnerWrapper />
  }

  if (hasFeedError) {
    return (
      <ErrorView
        className="mt-14"
        title={translate('flashMessage.somethingWentWrongError')}
        illustration={IllustrationChoices.EMPTY}
        buttons={[
          {
            id: 'retry-feed',
            button: {
              label: translate('common.actions.tryAgain'),
              onClick: () => retryFeed(),
              variant: ButtonVariant.PRIMARY,
            },
          },
        ]}
      />
    )
  }

  if (isPendingRequestView) {
    const pendingRequestErrorView = {
      title: translate('feedView.emptyState.pendingRequest.title'),
      subtitle: translate('feedView.emptyState.pendingRequest.subtitle'),
      illustration: IllustrationChoices.WAITING,
      buttons: [
        {
          id: uuid(),
          button: {
            label: translate('feedView.emptyState.pendingRequest.buttonTitle', {
              context: i18nSocietyType(undefined),
            }),
            onClick: () => navigate(reverseUrl('find-society')),
            variant: ButtonVariant.PRIMARY,
          },
        },
      ],
    }

    return <ErrorView {...pendingRequestErrorView} />
  }

  if (noSocieties) {
    const noSocietyErrorView = {
      title: translate('feedView.emptyState.noSociety.title', {
        context: i18nSocietyType(undefined),
      }),
      subtitle: translate('feedView.emptyState.noSociety.subtitle'),
      illustration: IllustrationChoices.EMPTY,
      buttons: [
        {
          id: uuid(),
          button: {
            label: translate('feedView.emptyState.noSociety.buttonTitle', {
              context: i18nSocietyType(undefined),
            }),
            onClick: () => navigate(reverseUrl('find-society')),
            variant: ButtonVariant.DEFAULT,
          },
        },
        {
          id: uuid(),

          button: {
            label: translate(
              'feedView.emptyState.noSociety.createSocietyButton',
              { context: i18nSocietyType(undefined) }
            ),
            onClick: () => navigate(reverseUrl('create-society:pick-type')),
            variant: ButtonVariant.PRIMARY,
          },
        },
      ],
    }

    return (
      <ErrorView
        {...noSocietyErrorView}
        buttonClassName="flex flex-wrap justify-center gap-4"
      />
    )
  }

  if (isAllSocietiesLockedForUserErrorView) {
    const allSocietiesLockedForUserErrorView = {
      title: translate('invoiceView.basic.locked.title', {
        context: i18nSocietyType(undefined),
      }),
      subtitle: translate('invoiceView.basic.locked.subtitle'),
      illustration: IllustrationChoices.EMPTY,
      className: 'px-4 py-16',
      buttons: [
        {
          id: 'pay-annual-fee',
          button: {
            label: translate('common.button.toPayment'),
            onClick: () =>
              navigate(
                reverseUrl('management:billing-payment', {
                  id: societiesWhereUserCanPost[0]._id,
                })
              ),
            variant: ButtonVariant.PRIMARY,
          },
        },
      ],
    }

    return <ErrorView {...allSocietiesLockedForUserErrorView} />
  }

  return (
    <>
      <ScheduledPostsBanner userId={userId} />
      {societiesWhereUserCanPost.length > 0 && (
        <PostCreateButton
          userId={userId}
          onClick={() => openCreatePostModal()}
        />
      )}
      <Feed
        userCanPost={societiesWhereUserCanPost.length > 0}
        fetchNextPage={fetchNextPage}
        isFetching={isFetching}
        posts={posts}
        filterActive={!!isFilterActive}
        openCreatePostModal={openCreatePostModal}
      />
      {showCreatePostModal && (
        <CreateEditPostModal
          show={showCreatePostModal}
          close={closeCreatePostModal}
        />
      )}
    </>
  )
}

export { FeedContainer }
